import React, { useCallback, useEffect, useMemo, useState } from "react";
import { LocalDateTime } from "@js-joda/core";
import { GanttProps, IGanttRangeRelative } from "components/ComponentType";
import {
  IProductTemplate,
  ITableDataEntry,
} from "components/dashboard/product-template-table/utils/interfaces";
import useDefaultPlotlyConfig from "hooks/useDefaultPlotlyConfig";
import { Config, Data, Layout, PlotData, PlotRelayoutEvent, Shape } from "plotly.js";
import Plot from "react-plotly.js";
import { T } from "services/lang/LanguageService";
import { DEFAULT_DATE_FORMATTER } from "utils/constants";
import {
  ProductTemplateSortingStrategy,
  ProductTemplateSortingStrategyOptionsOrder,
} from "utils/enums";
import {
  convertTimeSpanToString,
  formatDate,
  formatDateDuration,
  getTimestamp,
  TimeSpan,
} from "utils/time-utils";
import copyChartToClipBoard from "../../chartUtils";
import { clipboardIcon } from "../../icons";

type GanttChartProps = GanttProps & {
  templateData: ITableDataEntry[];
  configData: IProductTemplate | undefined;
};
const OFFSET_HIGH = 0.4;
const MAX_VISIBLE_LABELS = 40;
const ROW_HEIGHT_PX = 30;
const MIN_CHART_HEIGHT = 150;

const GanttChart: React.FC<GanttChartProps> = ({
  templateData,
  configData,
  aggregateY,
  ...props
}) => {
  const defaultConfig = useDefaultPlotlyConfig();
  const getAdditionalInfo = useCallback(
    (tableData: ITableDataEntry) => {
      return Object.values(tableData.entries).reduce((arr, val) => {
        if (val.type === "TIMESPAN") {
          return `${arr}<br>${
            configData?.data?.attributes.find(a => a.referenceName === val.referenceName)
              ?.displayName
          }: ${convertTimeSpanToString(val.value as TimeSpan)}`;
        }
        if (val.type !== "INSTANT") {
          return `${arr}<br>${configData?.data?.attributes.find(a => a.referenceName === val.referenceName)?.displayName}: ${val.value}`;
        }
        return arr;
      }, "");
    },
    [configData?.data?.attributes]
  );

  const assignOverlapTrackIndex = (
    entry: ITableDataEntry,
    all: ITableDataEntry[],
    gr: IGanttRangeRelative
  ): number => {
    const getTimeRange = (e: ITableDataEntry) => {
      return {
        start: getTimestamp(e.entries[gr.startDateColumn!]?.value),
        end: getTimestamp(e.entries[gr.endDateColumn!]?.value),
      };
    };

    const isNonOverlapping = (
      s: number | null,
      eEnd: number | null,
      exStart: number | null,
      exEnd: number | null
    ): boolean => {
      return (
        eEnd == null ||
        (exStart != null && exEnd != null && eEnd <= exStart) ||
        (s != null && exEnd != null && s >= exEnd)
      );
    };

    const placedTracks: ITableDataEntry[][] = [];

    all.forEach(candidate => {
      const { start: s, end: eEnd } = getTimeRange(candidate);

      const trackIndex = placedTracks.findIndex(track =>
        track.every(existing => {
          const { start: exStart, end: exEnd } = getTimeRange(existing);
          return isNonOverlapping(s, eEnd, exStart, exEnd);
        })
      );

      if (trackIndex !== -1) {
        placedTracks[trackIndex].push(candidate);
      } else {
        placedTracks.push([candidate]);
      }
    });

    const foundIndex = placedTracks.findIndex(track => track.includes(entry));
    return foundIndex !== -1 ? foundIndex : placedTracks.length;
  };

  const sortedTraces = useCallback((data: Partial<PlotData>[]): Partial<PlotData>[] => {
    return [...data].sort((a, b) => {
      const aX = Array.isArray(a.x) ? a.x[0] : a.x;
      const bX = Array.isArray(b.x) ? b.x[0] : b.x;

      const aStart = getTimestamp(aX) ?? 0;
      const bStart = getTimestamp(bX) ?? 0;

      return aStart - bStart;
    });
  }, []);

  const sortedTemplateData = useMemo(
    () =>
      templateData.sort((a, b) => {
        const sortingStrategy = configData?.data?.config?.sortingStrategy;
        const sortingReversed = configData?.data?.config?.sortingReversed;
        const sortColumn = configData?.data?.config?.sortingStrategyOptions?.columns?.[0];
        switch (sortingStrategy) {
          case ProductTemplateSortingStrategy.CREATION_DATE:
            if (sortingReversed) return b.dateCreated.localeCompare(a.dateCreated);
            return a.dateCreated.localeCompare(b.dateCreated);
          case ProductTemplateSortingStrategy.ADVANCED:
            if (sortColumn?.order === ProductTemplateSortingStrategyOptionsOrder.DESCENDING) {
              if (typeof b.entries?.[sortColumn.column]?.value === "number") {
                return (
                  (a.entries?.[sortColumn.column]?.value as number) -
                  (b.entries?.[sortColumn.column]?.value as number)
                );
              }
              return a.entries?.[sortColumn.column]?.value
                ?.toString()
                .localeCompare(b.entries?.[sortColumn.column]?.value?.toString());
            }
            if (sortColumn?.order === ProductTemplateSortingStrategyOptionsOrder.ASCENDING) {
              if (typeof a.entries?.[sortColumn.column]?.value === "number") {
                return (
                  (b.entries?.[sortColumn.column]?.value as number) -
                  (a.entries?.[sortColumn.column]?.value as number)
                );
              }
              return b.entries?.[sortColumn.column]?.value
                ?.toString()
                .localeCompare(a.entries?.[sortColumn.column]?.value?.toString());
            }
            return a.keyValue.localeCompare(b.keyValue);
          case ProductTemplateSortingStrategy.KEY_AND_META_KEY:
            if (sortingReversed) return b.keyValue.localeCompare(a.keyValue);
            return a.keyValue.localeCompare(b.keyValue);
          default:
            return a.keyValue.localeCompare(b.keyValue);
        }
      }),
    [
      configData?.data?.config?.sortingReversed,
      configData?.data?.config?.sortingStrategy,
      configData?.data?.config?.sortingStrategyOptions?.columns,
      templateData,
    ]
  );

  const getLabel = useCallback(
    (td: ITableDataEntry) =>
      td.entries[props.columnOverrideYLabels]?.value?.toString() ?? td.stringRepresentation ?? "",
    [props.columnOverrideYLabels]
  );

  const getRowStartTime = (acc: ITableDataEntry): number | null => {
    const allStartTimestamps = [
      ...props.ganttRanges.map(gr => acc.entries[gr.startDateColumn!]?.value?.toString()),
      ...props.ganttTimestamps.map(gt => acc.entries[gt.dateColumn!]?.value?.toString()),
    ].filter(Boolean) as string[];

    if (allStartTimestamps.length === 0) return null;

    return Math.min(...allStartTimestamps.map(ts => new Date(ts).getTime()));
  };

  const getRelativeOffsetForGroup = (
    groupStart: string,
    startTimeStr?: string,
    endTimeStr?: string
  ): [number, number] => {
    if (!startTimeStr || !groupStart) return [0, 0];
    const groupStartTime = new Date(groupStart).getTime();
    const start = new Date(startTimeStr).getTime();
    const end = endTimeStr ? new Date(endTimeStr).getTime() : start;
    return [start - groupStartTime, end - groupStartTime];
  };

  const maxDurationMs = useMemo(() => {
    return Math.max(
      ...sortedTemplateData.map(entry => {
        const rowStart = getRowStartTime(entry);
        const allEndTimestamps = [
          ...props.ganttRanges.map(gr => entry.entries[gr.endDateColumn!]?.value?.toString()),
          ...props.ganttTimestamps.map(gt => entry.entries[gt.dateColumn!]?.value?.toString()),
        ].filter(Boolean) as string[];

        const latestEnd = Math.max(...allEndTimestamps.map(ts => new Date(ts).getTime()));

        return latestEnd - (rowStart ?? latestEnd);
      })
    );
  }, [sortedTemplateData, props.ganttRanges, props.ganttTimestamps]);

  const hourTicks = useMemo(() => {
    const maxHours = Math.ceil(maxDurationMs / 3600000);
    return {
      tickvals: Array.from({ length: maxHours + 1 }, (_, i) => i * 3600000),
      ticktext: Array.from({ length: maxHours + 1 }, (_, i) => `${i}h`),
    };
  }, [maxDurationMs]);

  const sortedGroups = useMemo(() => {
    if (!sortedTemplateData || !props?.startColumn || !props?.endColumn || !props?.isRelative)
      return [];

    const groupsMap = new Map<string, ITableDataEntry[]>();
    sortedTemplateData.forEach(item => {
      const start =
        props.startColumn !== undefined ? item.entries[props.startColumn]?.value : undefined;
      const end = props.endColumn !== undefined ? item.entries[props.endColumn]?.value : undefined;

      if (!start || !end) return;

      const groupKey = `${start}__${end}`;
      if (!groupsMap.has(groupKey)) {
        groupsMap.set(groupKey, []);
      }

      groupsMap.get(groupKey)?.push(item);
    });

    const grouped = Array.from(groupsMap.entries()).map(([groupKey, items]) => {
      const [start, end] = groupKey.split("__");
      return {
        groupKey,
        start,
        end,
        items,
      };
    });

    return grouped;
  }, [sortedTemplateData, props?.startColumn, props?.endColumn]);

  const groupTrackCounts = useMemo(() => {
    return sortedGroups.map(group => {
      const itemsWithDates = group.items.filter(item =>
        props.ganttRanges.some(
          gr => item.entries[gr.startDateColumn!] && item.entries[gr.endDateColumn!]
        )
      );

      const ranges = itemsWithDates
        .map(item => {
          const gr = props.ganttRanges.find(
            grp => item.entries[grp.startDateColumn!] && item.entries[grp.endDateColumn!]
          );

          if (!gr) return null;

          return {
            item,
            start: getTimestamp(item.entries[gr.startDateColumn!]?.value),
            end: getTimestamp(item.entries[gr.endDateColumn!]?.value),
          };
        })
        .filter((r): r is { item: ITableDataEntry; start: number; end: number } => r !== null);

      const placedTracks: { start: number; end: number }[][] = [];

      ranges.forEach(({ start, end }) => {
        const trackIndex = placedTracks.findIndex(track =>
          track.every(({ start: s, end: e }) => e <= start || s >= end)
        );
        if (trackIndex !== -1) {
          placedTracks[trackIndex].push({ start, end });
        } else {
          placedTracks.push([{ start, end }]);
        }
      });

      return placedTracks.length;
    });
  }, [sortedGroups, props.ganttRanges]);

  const groupYOffsets = useMemo(() => {
    const offsets: number[] = [];
    let currentOffset = 1;
    for (let i = 0; i < groupTrackCounts.length; i++) {
      offsets.push(currentOffset);
      currentOffset += groupTrackCounts[i];
    }
    return offsets;
  }, [groupTrackCounts]);

  const totalChartRows = useMemo(() => {
    const lastOffset = groupYOffsets[groupYOffsets.length - 1] ?? 0;
    const lastGroupTracks = groupTrackCounts[groupTrackCounts.length - 1] ?? 0;
    return lastOffset + lastGroupTracks;
  }, [groupYOffsets, groupTrackCounts]);

  const groupDividers = useMemo(() => {
    if (!props.isRelative) return [];

    return sortedGroups
      .map((_group, i) => {
        if (i === sortedGroups.length - 1) return [];

        const currentOffset = groupYOffsets[i];
        const tracksInCurrentGroup = groupTrackCounts[i];

        const y = currentOffset + tracksInCurrentGroup - 0.5;

        return {
          type: "line",
          xref: "paper",
          yref: "y",
          x0: 0,
          x1: 1,
          y0: y,
          y1: y,
          line: {
            color: "#999",
            width: 2,
            dash: "dot",
          },
          layer: "below",
        };
      })
      .filter(Boolean);
  }, [props.isRelative, sortedGroups, groupYOffsets, groupTrackCounts]);

  const ranges: Partial<PlotData>[] = useMemo(() => {
    const legends = new Set<string>();
    const yLabels: string[] = [];

    if (props.isRelative) {
      return sortedGroups.flatMap((group, groupIndex) => {
        const sortedItems = [...group.items].sort((a, b) => {
          const aVal = a.entries[props.startColumn!]?.value;
          const bVal = b.entries[props.startColumn!]?.value;

          const aStart = getTimestamp(aVal) ?? 0;
          const bStart = getTimestamp(bVal) ?? 0;

          return aStart - bStart;
        });

        return sortedItems.flatMap(acc => {
          return props.ganttRanges
            .filter(gr => {
              return Object.values(acc.entries).some(entry => entry.value === gr.valueInEntry);
            })
            .map(gr => {
              const startDate = acc.entries[gr.startDateColumn!]?.value?.toString();
              const endDate = acc.entries[gr.endDateColumn!]?.value?.toString();
              if (!startDate) return null;

              const [x0, x1] = getRelativeOffsetForGroup(group.start, startDate, endDate);
              const trackIndex = assignOverlapTrackIndex(acc, group.items, gr);

              const baseY = groupYOffsets[groupIndex];

              const y0 = baseY + trackIndex - OFFSET_HIGH;
              const y1 = baseY + trackIndex + OFFSET_HIGH;

              const title = gr.displayValue ?? "";
              const hoverInfo =
                `${title}<br>` +
                `${T("layout.start")}: ${formatDate(startDate)}<br>` +
                `${T("layout.end")}: ${formatDate(endDate)}<br>` +
                `${T("word.duration")}: ${formatDateDuration(startDate, endDate)}` +
                `${configData && getAdditionalInfo(acc)}`;

              const trace: Partial<PlotData> = {
                mode: "lines",
                fill: "toself",
                fillcolor: gr.color,
                opacity: 1,
                name: title,
                x: [x0, x1, x1, x0, x0],
                y: [y0, y0, y1, y1, y0],
                line: { color: gr.color },
                showlegend: !legends.has(title),
                legendgroup: title,
                text: hoverInfo,
                hoverinfo: "text",
                hoverlabel: {
                  bgcolor: gr.color,
                },
              };
              legends.add(title);
              return trace;
            })
            .filter(Boolean) as Partial<PlotData>[];
        });
      });
    }

    return sortedTemplateData.reduce((array, acc, index) => {
      const label = getLabel(acc);
      yLabels.push(label);
      const indexOfLabel = aggregateY ? [...yLabels.values()].indexOf(label) : index;

      props.ganttRanges.forEach(gr => {
        const startDate = acc.entries[gr.startDateColumn!]?.value?.toString();
        const endDate =
          acc.entries[gr.endDateColumn!]?.value?.toString() ?? new Date().toISOString();
        const title = gr.title ?? "";
        if (startDate) {
          const [x0, x1] = props.isRelative
            ? getRelativeOffsetForGroup(startDate, startDate, endDate)
            : [formatDate(startDate), formatDate(endDate)];

          const hoverInfo =
            `${title}<br>` +
            `${T("layout.start")}: ${formatDate(startDate)}<br>` +
            `${T("layout.end")}: ${formatDate(endDate)}<br>` +
            `${T("word.duration")}: ${formatDateDuration(startDate, endDate)}` +
            `${configData && getAdditionalInfo(acc)}`;

          const trace: Partial<PlotData> = {
            mode: "lines",
            fill: "toself",
            fillcolor: gr.color,
            opacity: 1,
            name: title,
            x: [x0, x1, x1, x0, x0],
            y: [
              indexOfLabel - OFFSET_HIGH,
              indexOfLabel - OFFSET_HIGH,
              indexOfLabel + OFFSET_HIGH,
              indexOfLabel + OFFSET_HIGH,
              indexOfLabel - OFFSET_HIGH,
            ],
            line: { color: gr.color },
            showlegend: !legends.has(title),
            legendgroup: title,
            text: hoverInfo,
            hoverinfo: "text",
            hoverlabel: {
              bgcolor: gr.color,
            },
          };

          const mark: Partial<PlotData> = {
            name: title,
            x: [endDate, endDate],
            y: [indexOfLabel, indexOfLabel],
            marker: { color: gr.color, size: 10 },
            opacity: 0,
            showlegend: false,
            text: hoverInfo,
            hoverinfo: "text",
          };

          legends.add(title);
          array.push(trace);
          array.push(mark);
        }
      });

      return array;
    }, [] as Partial<PlotData>[]);
  }, [
    aggregateY,
    configData,
    getAdditionalInfo,
    getLabel,
    props.ganttRanges,
    sortedTemplateData,
    sortedGroups,
    props.isRelative,
  ]);

  const timestamps: Partial<PlotData>[] = useMemo(() => {
    const legends = new Set<string>();
    const yLabels = new Set<string>();

    if (props.isRelative) {
      return sortedGroups.flatMap((group, groupIndex) => {
        const label = `${formatDate(group.start)} - ${formatDate(group.end)}`;
        yLabels.add(label);

        return group.items.flatMap(acc => {
          return props.ganttTimestamps
            .map(gt => {
              const date = acc.entries[gt.dateColumn!]?.value?.toString();
              if (!date) return null;

              const [offset] = getRelativeOffsetForGroup(group.start, date);
              const baseY = groupYOffsets[groupIndex];
              const midY = baseY + (groupTrackCounts[groupIndex] - 1) / 2;
              const trace: Partial<PlotData> = {
                name: gt.title ?? "",
                x: [offset, offset],
                y: [midY, midY],
                marker: { color: gt.color, symbol: gt.icon ?? "circle", size: 10 },
                line: { color: "transparent" },
                showlegend: !legends.has(gt.title ?? ""),
                legendgroup: gt.title ?? "",
                text:
                  `${gt.title}<br>` +
                  `${T("word.date")}: ${formatDate(date)}` +
                  `${configData && !props.isRelative ? getAdditionalInfo(acc) : ""}`,
                hoverinfo: "text",
              };

              legends.add(gt.title ?? "");
              return trace;
            })
            .filter(Boolean) as Partial<PlotData>[];
        });
      });
    }

    return sortedTemplateData.reduce((array, acc, index) => {
      const label = getLabel(acc);
      yLabels.add(label);
      const indexOfLabel = aggregateY ? [...yLabels.values()].indexOf(label) : index;

      props.ganttTimestamps.forEach(gt => {
        const date = acc.entries[gt.dateColumn!]?.value?.toString();
        if (!date) return;

        const [x] = [formatDate(date)];
        const title = gt.title ?? "";

        const trace: Partial<PlotData> = {
          name: title,
          x: [x, x],
          y: [indexOfLabel, indexOfLabel],
          marker: { color: gt.color, symbol: gt.icon ?? "circle", size: 10 },
          line: { color: "transparent" },
          showlegend: !legends.has(title),
          legendgroup: title,
          text:
            `${gt.title}<br>` +
            `${T("word.date")}: ${formatDate(date)}` +
            `${configData && getAdditionalInfo(acc)}`,
          hoverinfo: "text",
        };

        legends.add(title);
        array.push(trace);
      });

      return array;
    }, [] as Partial<PlotData>[]);
  }, [
    aggregateY,
    configData,
    getAdditionalInfo,
    getLabel,
    props.ganttTimestamps,
    props.isRelative,
    sortedTemplateData,
    sortedGroups,
  ]);

  const currentTime = LocalDateTime.now().format(DEFAULT_DATE_FORMATTER).toString();
  const mappedLabels = useMemo(
    () => sortedTemplateData.map(getLabel),
    [getLabel, sortedTemplateData]
  );

  const yLabels = useMemo(() => {
    if (props.isRelative) {
      return sortedGroups.map(group => `${formatDate(group.start)} - ${formatDate(group.end)}`);
    }

    return aggregateY ? [...new Set(mappedLabels)] : mappedLabels;
  }, [props.isRelative, sortedGroups, aggregateY, mappedLabels]);

  const leftMargin = useMemo(() => Math.max(...yLabels.map(y => y.length)) * 7, [yLabels]);

  const currentTimeLine: Partial<PlotData> = {
    x: [
      ...Array(yLabels.length + 2)
        .fill(0)
        .map(() => currentTime),
    ],
    y: [-1, ...yLabels.map((_, ind) => ind), yLabels.length],
    mode: "lines",
    line: { color: "#D95648", width: 2, dash: "dash" },
    opacity: 0.5,
    showlegend: false,
    text: `${T("word.current-time-line")}: ${currentTime}`,
    hoverinfo: "text",
  };

  const data: Data[] = props.isRelative
    ? sortedTraces([...ranges, ...timestamps, currentTimeLine])
    : [...ranges, ...timestamps, currentTimeLine];

  const layout: Partial<Layout> = useMemo(
    () => ({
      xaxis: {
        type: props.isRelative ? "linear" : "date",
        showgrid: true,
        zeroline: false,
        ...(props.isRelative ? hourTicks : {}),
      },
      yaxis: {
        showgrid: true,
        ticktext: yLabels,
        showticklabels: yLabels.length <= MAX_VISIBLE_LABELS,
        tickvals: props.isRelative ? groupYOffsets : yLabels.map((_td, i) => i),
        zeroline: false,
        autorange: props.isRelative ? false : undefined,
        range: props.isRelative ? [0, totalChartRows] : undefined,
      },
      height: props.isRelative
        ? Math.max(totalChartRows * ROW_HEIGHT_PX, MIN_CHART_HEIGHT)
        : undefined,
      hovermode: "closest",
      showlegend: props.showLegends ?? true,
      legend: {
        orientation: "h",
        y: props.isRelative ? -(20 / (totalChartRows * ROW_HEIGHT_PX)) : -0.15,
      },
      margin: {
        l: yLabels.length <= MAX_VISIBLE_LABELS ? leftMargin : 0,
        r: 0,
        b: 20,
        t: 20,
      },
      shapes: groupDividers as Partial<Shape>[],
    }),
    [yLabels, props.height, props.showLegends, leftMargin, hourTicks, groupYOffsets, groupDividers]
  );

  const [currentLayout, setCurrentLayout] = useState(layout);
  useEffect(() => {
    setCurrentLayout(layout);
  }, [layout]);

  const handleZoom = useCallback(
    (eventData: Readonly<PlotRelayoutEvent>) => {
      const yRange0 = eventData["yaxis.range[0]"] ?? (eventData["yaxis.range"]?.[0] as number);
      const yRange1 = eventData["yaxis.range[1]"] ?? (eventData["yaxis.range"]?.[1] as number);
      const yAutoRange = eventData["yaxis.autorange"] ?? false;
      if (!yAutoRange && !yRange0) return;
      const visibleLabels = Math.abs(yRange1 - yRange0);
      const showLabels = visibleLabels <= MAX_VISIBLE_LABELS;
      let ml = 0;
      if (yAutoRange) {
        ml = yLabels.length <= MAX_VISIBLE_LABELS ? leftMargin : 0;
      }
      if (showLabels) {
        ml = leftMargin;
      }
      const newLayout = {
        ...currentLayout,
        yaxis: {
          ...currentLayout.yaxis,
          showticklabels: yAutoRange ? yLabels.length <= MAX_VISIBLE_LABELS : showLabels,
        },
        margin: {
          l: ml,
          r: 0,
          b: 20,
          t: 20,
        },
      };

      setCurrentLayout(newLayout);
    },
    [currentLayout, yLabels, leftMargin]
  );

  const chartConfig: Partial<Config> = {
    ...defaultConfig,
    modeBarButtonsToAdd: [
      {
        title: T("chart-config.copy-chart-to-clipboard"),
        name: "Copy chart to Clipboard",
        icon: clipboardIcon,
        click: copyChartToClipBoard,
      },
    ],
  };

  return (
    <Plot
      data={data}
      layout={currentLayout}
      config={chartConfig}
      className="w-full"
      useResizeHandler
      onRelayout={handleZoom}
    />
  );
};

export default GanttChart;
